import { useCallback, useMemo, useState } from "react";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import { pluralize } from "@common/helpers/string";
import { AccountRole } from "@common/types/domains/accounting/account";
import { EntityProfileType } from "@common/types/entity";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Drawer } from "@design-system/Drawer/Drawer";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import { ChartOfAccountsTemplateStepSubtitle } from "../ChartOfAccountsTemplateStepSubtitle";
import { IssueNavigationCommandIsland } from "../IssueNavigationCommandIsland";
import { useRowIssueNavigation } from "../useRowIssueNavigation";
import {
    RequiredAccountsCallbacks,
    RequiredAccountsOptions,
} from "./cells/RequiredAccountAssignedCell";
import { CreateRoleAccountModal } from "./CreateRoleAccountModal";
import {
    buildDefaultRoleAccountValue,
    buildDefaultRoleAccountValues,
    buildRequiredAccountRows,
    CreateRoleAccountValues,
    isCodeTextTaken,
    RequiredAccountRow,
    RoleMappingAccount,
    suggestCreatedRoleAccountCodeText,
} from "./requiredAccountsMapping";
import {
    RequiredAccountsColumn,
    requiredAccountsView,
    requiredAccountsViewConfig,
} from "./requiredAccountsViewConfig";
import styles from "./RequiredAccountsMapping.module.scss";

interface Props {
    open: boolean;
    accounts: readonly RoleMappingAccount[];
    codeContextAccounts?: ReadonlyArray<
        Pick<RoleMappingAccount, "id" | "codeText">
    >;
    nameContextAccounts?: ReadonlyArray<Pick<RoleMappingAccount, "name">>;
    entityLegalType: EntityProfileType;
    confirmLabel: string;
    isConfirming: boolean;
    isDiscardPromptOpen: boolean;
    onAssign: (params: { role: AccountRole; accountId: string }) => void;
    onCreateAccount: (values: CreateRoleAccountValues) => void;
    onClose: () => void;
    onConfirm: () => Promise<void> | void;
}

export const RequiredAccountsMappingDrawer = ({
    open,
    accounts,
    codeContextAccounts = accounts,
    nameContextAccounts = accounts,
    entityLegalType,
    confirmLabel,
    isConfirming,
    isDiscardPromptOpen,
    onAssign,
    onCreateAccount,
    onClose,
    onConfirm,
}: Props) => {
    const [creatingAccount, setCreatingAccount] = useState<{
        role: AccountRole;
        initialName?: string;
    } | null>(null);
    const suggestCodeText = useCallback(
        (type: AccountType) =>
            suggestCreatedRoleAccountCodeText({
                type,
                accounts: codeContextAccounts,
            }),
        [codeContextAccounts],
    );
    const checkCodeTextTaken = useCallback(
        (codeText: string) =>
            isCodeTextTaken({ codeText, accounts: codeContextAccounts }),
        [codeContextAccounts],
    );
    const rows = useMemo(
        () =>
            buildRequiredAccountRows({
                accounts,
                nameContextAccounts,
                entityLegalType,
            }),
        [accounts, nameContextAccounts, entityLegalType],
    );
    const defaultAccountValues = useMemo(
        () =>
            buildDefaultRoleAccountValues({
                rows,
                entityLegalType,
                codeContextAccounts,
            }),
        [rows, entityLegalType, codeContextAccounts],
    );
    const unassignedRows = useMemo(
        () =>
            rows.filter(({ assignedAccountId }) => assignedAccountId === null),
        [rows],
    );
    const unassignedRoles = useMemo(
        () => unassignedRows.map(({ role }) => role),
        [unassignedRows],
    );
    const { tableRef, currentIndex, navigateTo } = useRowIssueNavigation({
        rowIds: unassignedRoles,
        field: "account",
    });

    const hasDefaultAccountsToCreate = defaultAccountValues.length > 0;
    const isCreateDefaultAccountsDisabled =
        isConfirming || isDiscardPromptOpen || creatingAccount !== null;
    const createDefaultAccounts = useCallback(
        () => defaultAccountValues.forEach((values) => onCreateAccount(values)),
        [onCreateAccount, defaultAccountValues],
    );

    const tableMeta = useMemo(
        (): ViewTableMeta<
            RequiredAccountsCallbacks,
            RequiredAccountsOptions
        > => ({
            callbacks: {
                onAssign,
                onCreateAccount: setCreatingAccount,
                onCreateDefaultAccount: ({ role }) =>
                    onCreateAccount(
                        buildDefaultRoleAccountValue({
                            role,
                            entityLegalType,
                            codeContextAccounts,
                        }),
                    ),
                onCreateDefaultAccounts: createDefaultAccounts,
            },
            options: {
                isCreateDefaultAccountsDisabled,
                hasDefaultAccountsToCreate,
            },
        }),
        [
            onAssign,
            onCreateAccount,
            entityLegalType,
            codeContextAccounts,
            createDefaultAccounts,
            isCreateDefaultAccountsDisabled,
            hasDefaultAccountsToCreate,
        ],
    );

    return (
        <Drawer
            open={open}
            onClose={onClose}
            direction="right"
            size="fullScreen"
            noPadding
            headerAddonLeft="Required system accounts mapping"
        >
            <div className={styles.drawerContent}>
                <ChartOfAccountsTemplateStepSubtitle>
                    Choose the accounts Kick uses to automate your work. Some
                    only apply in certain setups, like intercompany for multiple
                    entities. Assign them anyway so the template works for every
                    client. Account names can vary by entity.
                </ChartOfAccountsTemplateStepSubtitle>
                <div className={styles.tableContainer}>
                    <ViewTable<
                        RequiredAccountRow,
                        RequiredAccountsColumn,
                        never
                    >
                        ref={tableRef}
                        data={rows}
                        view={requiredAccountsView}
                        config={requiredAccountsViewConfig}
                        tableMeta={tableMeta}
                        getRowClassName={() => styles.growingRow}
                        columnBorders
                        footer={
                            <IssueNavigationCommandIsland
                                completeStatus={
                                    <span className={styles.islandStatus}>
                                        All {rows.length}{" "}
                                        {pluralize("account", rows.length)}{" "}
                                        assigned
                                    </span>
                                }
                                issueLabel="Unassigned"
                                issueCount={unassignedRows.length}
                                currentIssueIndex={currentIndex}
                                currentIssueMessage={
                                    unassignedRows[currentIndex]
                                        ?.requiredAccountName
                                }
                                onNavigateIssue={navigateTo}
                            >
                                {hasDefaultAccountsToCreate && (
                                    <Button
                                        variant="primary"
                                        size="md"
                                        disabled={
                                            isCreateDefaultAccountsDisabled
                                        }
                                        onClick={createDefaultAccounts}
                                    >
                                        Create default accounts
                                    </Button>
                                )}
                                <ButtonWithLoader
                                    variant="primary"
                                    size="md"
                                    loading={isConfirming}
                                    disabled={
                                        unassignedRows.length > 0 ||
                                        isDiscardPromptOpen ||
                                        creatingAccount !== null
                                    }
                                    hotKey="mod+enter"
                                    hotKeyOptions={{
                                        description: confirmLabel,
                                    }}
                                    hotKeyDisplayPosition="inline"
                                    onClick={onConfirm}
                                >
                                    {confirmLabel}
                                </ButtonWithLoader>
                            </IssueNavigationCommandIsland>
                        }
                    />
                </div>
            </div>
            {creatingAccount && (
                <CreateRoleAccountModal
                    role={creatingAccount.role}
                    initialName={creatingAccount.initialName}
                    entityLegalType={entityLegalType}
                    suggestCodeText={suggestCodeText}
                    isCodeTextTaken={checkCodeTextTaken}
                    onClose={() => setCreatingAccount(null)}
                    onCreate={(values) => {
                        onCreateAccount(values);
                        setCreatingAccount(null);
                    }}
                />
            )}
        </Drawer>
    );
};
